// Import necessary types
import { Example } from "../models/ExampleModel";
import { ExplanationModel } from "../models/ExplanationModel";
import { VerbConjugation } from "../models/VerbConjugationModel";

// Explanation: What is the Imperfect Tense?
export const preteritoImperfectoExplanation: ExplanationModel = {
  title: "📖 What is Pretérito Imperfecto?",
  description:
    "The Imperfect Tense is used to describe past actions that don’t have a clear beginning or end. It is used in descriptions, habitual actions, and actions in progress in the past.",
  bulletPoints: [
    "Describes past actions without a defined start or end.",
    "Used to talk about habits and actions in progress.",
    "Can be used for descriptions of people, places, and situations.",
    "Often used in combination with the Preterite Tense to differentiate context and specific actions.",
  ],
};

// Examples of the Imperfect Tense
export const preteritoImperfectoExamples: Example[] = [
  {
    sentence: "Cuando era niño, solía jugar en el parque todos los días.",
    explanation:
      "👦 Habitual Action in the Past – 'When I was a child, I used to play in the park every day.' The imperfect tense (era, jugaba) expresses a repeated or habitual past action.",
  },
  {
    sentence: "Mientras estudiaba, mi hermano veía la televisión.",
    explanation:
      "📖📺 Simultaneous Actions – 'While I was studying, my brother was watching TV.' The imperfect is used to describe two actions happening at the same time in the past (estudiaba, veía).",
  },
  {
    sentence: "Antes, vivíamos en Madrid.",
    explanation:
      "🏠 Past State or Habit – 'Before, we used to live in Madrid.' The imperfect tense (vivíamos) describes a continuous or habitual past situation.",
  },
  {
    sentence: "Siempre me gustaba leer antes de dormir.",
    explanation:
      "📚 Repeated Preference – 'I always liked to read before sleeping.' The verb gustaba is in the imperfect because it describes a continuous preference or state in the past.",
  },
  {
    sentence: "Los domingos íbamos a la playa con mi familia.",
    explanation:
      "🏖️ Routine in the Past – 'On Sundays, we used to go to the beach with my family.' The verb íbamos shows a past habit or repeated action.",
  },
  {
    sentence: "De pequeño, tenía un perro llamado Max.",
    explanation:
      "🐶 Past Description – 'When I was little, I had a dog named Max.' The verb tenía describes a past possession or state, which is common with the imperfect.",
  },
];

// Regular Verbs in the Imperfect Tense
export const preteritoImperfectoRegular: VerbConjugation[] = [
  { verb: "Hablar", conjugations: ["hablaba", "hablabas", "hablaba", "hablábamos", "hablabais", "hablaban"] },
  { verb: "Comer", conjugations: ["comía", "comías", "comía", "comíamos", "comíais", "comían"] },
  { verb: "Vivir", conjugations: ["vivía", "vivías", "vivía", "vivíamos", "vivíais", "vivían"] },
  { verb: "Haber", conjugations: ["había", "habías", "había", "habíamos", "habíais", "habían"] },
  { verb: "Estar", conjugations: ["estaba", "estabas", "estaba", "estábamos", "estabais", "estaban"] },
  { verb: "Tener", conjugations: ["tenía", "tenías", "tenía", "teníamos", "teníais", "tenían"] },
  { verb: "Dar", conjugations: ["daba", "dabas", "daba", "dábamos", "dabais", "daban"] },
  { verb: "Saber", conjugations: ["sabía", "sabías", "sabía", "sabíamos", "sabíais", "sabían"] },
  { verb: "Poder", conjugations: ["podía", "podías", "podía", "podíamos", "podíais", "podían"] },
]

// Irregular Verbs in the Imperfect Tense
export const preteritoImperfectoIrregulars: VerbConjugation[] = [
  { verb: "Ser", conjugations: ["era", "eras", "era", "éramos", "erais", "eran"] },
  { verb: "Ir", conjugations: ["iba", "ibas", "iba", "íbamos", "ibais", "iban"] },
  { verb: "Ver", conjugations: ["veía", "veías", "veía", "veíamos", "veíais", "veían"] },
]

// When to Use the Imperfect Tense
export const preteritoImperfectoUsageDetails: ExplanationModel = {
  title: "When to Use the Imperfect Tense",
  description: "The Imperfect Tense is used in the following contexts:",
  examples: [
    "✅ Habitual actions: Antes, siempre veíamos películas los sábados.\n(Before, we always used to watch movies on Saturdays.)",
    "✅ Descriptions in the past: La casa era grande y tenía un jardín hermoso.\n(The house was big and had a beautiful garden.)",
    "✅ Simultaneous actions: Mientras ella cocinaba, él ponía la mesa.\n(While she was cooking, he was setting the table.)",
    "✅ Emotional or physical states: Me sentía cansado después de trabajar todo el día.\n(I felt tired after working all day.)"
  ],
};

// Difference Between Imperfect and Preterite
export const preteritoImperfectoDifferences: ExplanationModel = {
  title: "📌 Difference Between Imperfect and Preterite Tenses",
  description:
    "The Imperfect Tense describes the context and habitual actions in the past, while the Preterite Tense expresses specific and completed actions.",
  examples: [
    "Imperfect: Cuando era niño, iba al cine cada semana. (Habitual action)",
    "Preterite: Ayer, fui al cine. (Specific action)",
    "Imperfect: La casa era antigua y tenía un jardín. (Description)",
    "Preterite: Compramos una casa nueva el año pasado. (Completed action)",
  ],
};
export const pastImperfectStoryPark = `
Amigo 1: 'Cuando era niño, siempre venía a este parque con mis abuelos.'
Amigo 2: '¡Qué bonito! Yo también jugaba aquí con mis hermanos los fines de semana.'
Amigo 1: 'Mis abuelos me traían en las tardes y me compraban helado después de jugar.'
Amigo 2: 'Sí, recuerdo que había un señor que vendía globos. Siempre le pedíamos uno.'
Amigo 1: 'Antes el parque estaba más lleno, ¿verdad? Había más niños y hacíamos carreras por todo el césped.'
Amigo 2: 'Y el carrusel funcionaba todo el día. Me encantaba montarme una y otra vez.'
ㅤㅤㅤㅤㅤㅤㅤ
📌 Imperfect tense is used to describe past habits, repeated actions, and background situations:
- 'venía', 'jugaba', 'me traían', 'compraban', 'recordaba', 'pedíamos', 'hacíamos', 'funcionaba', 'me encantaba'
-  These actions don’t describe a specific moment but rather ongoing or habitual actions in the past.
`;